import { useMutation } from '@apollo/client'
import { Calendar, FileFilled } from '@factorialco/f0-react/icons/app'
import {
  MeetingsOneOnOneSummaryDocument,
  MeetingsOneOnOneSummaryMutation,
} from 'generated/resources/graphql'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { ONE_ON_ONE_TEMPLATE_TYPE } from 'modules/meetings/lib/constants/AIQueryTypes'
import { errorBlock } from 'modules/meetings/lib/constants/TipTapBlock'
import { useMeeting } from 'modules/meetings/lib/hooks/useMeeting'

export type MeetingState = 'pre' | 'post'

interface UseNotesTextEditorConfigProps {
  meetingId?: string | number
  meetingState?: MeetingState
}

export const useNotesTextEditorConfig = ({
  meetingId,
  meetingState,
}: UseNotesTextEditorConfigProps) => {
  const { canUseAI } = useMeeting()

  const [oneOnOneSummaryFormattedContent] = useMutation<MeetingsOneOnOneSummaryMutation>(
    MeetingsOneOnOneSummaryDocument
  )

  const handleGenerateOneOnOneSummary = async (templateType: string) => {
    track(trackingEvents.meetings.MeetingNotesPostMeeetingAIBlockStart, {
      meetingId: Number(meetingId),
      templateType,
    })

    if (meetingId && canUseAI) {
      try {
        const { data } = await oneOnOneSummaryFormattedContent({
          variables: {
            meetingId: Number(meetingId),
            templateType,
          },
        })
        if (data?.meetingsMutations?.generateMeetingsAiSummary?.aiSummary?.formattedContent) {
          const formattedContent =
            data.meetingsMutations.generateMeetingsAiSummary.aiSummary.formattedContent
          const parsedContent = JSON.parse(formattedContent)
          track(trackingEvents.meetings.MeetingNotesPostMeeetingAIBlockSuccess, {
            meetingId: Number(meetingId),
            templateType,
          })
          return parsedContent
        }
      } catch (error) {
        track(trackingEvents.meetings.MeetingNotesPostMeeetingAIBlockError, {
          meetingId: Number(meetingId),
          templateType,
        })
      }
    }
    return errorBlock(i18n.t('meetings.notes.labels.errorBlock.text'))
  }

  const oneOnOneButtons = [
    meetingState === 'post' && {
      type: ONE_ON_ONE_TEMPLATE_TYPE.POST_SUMMARY,
      emoji: '🗒️',
      label: i18n.t('meetings.notes.labels.buttons.summary'),
      icon: FileFilled,
    },
    {
      type: ONE_ON_ONE_TEMPLATE_TYPE.PRE_SUMMARY,
      emoji: '🗓️',
      label: i18n.t('meetings.notes.labels.buttons.recap'),
      icon: Calendar,
    },
  ].filter(Boolean)

  const getAiBlockConfig = () => {
    return {
      title:
        meetingState === 'post'
          ? i18n.t('meetings.meeting.post_meeting_buttons.title')
          : i18n.t('meetings.meeting.pre_meeting_buttons.title'),
      onClick: async (type: string) => {
        return await handleGenerateOneOnOneSummary(type)
      },
      buttons: canUseAI ? oneOnOneButtons : [],
    }
  }

  return {
    titlePlaceholder: i18n.t('meetings.notes.labels.titlePlaceholder'),
    aiBlockConfig: getAiBlockConfig(),
  }
}
